import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Grid, Box, Typography, Chip, Divider, Stack,
  CircularProgress, Backdrop,
  Accordion, AccordionSummary, AccordionDetails,
  Tooltip, IconButton, ButtonBase,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Check, Info, ChartNoAxesCombined, Minus, Plus, Smile, Zap, Heart, Thermometer, Dumbbell, Activity, CircleDot } from "lucide-react";
import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../../../language";
import { API_URL } from "../../../../constants";
import { useAuth } from "../../../../provider";
import CustomDialogSmall from "../../../../components/Dialog/DiaCompSmall";
import CustomDialog from "../../../../components/Dialog/DiaCompMedium";
import TextInfoComp from "../ReuseComps/TextInfoComp";
import MarkerTrendChart from "../ReuseComps/MarkerTrendChart";
import HormoneChartPanel from "./HormoneCharts";
import { DateSelector } from "../../Cockpit/HealthCockpit";

const SUBSYSTEM_KEY = {
  "Steroid Hormones": "subsystemHormSter",
  "Protein Hormones": "subsystemHormProt",
};


const SUMMARY_DIMS = [
  { key: "mood",   labelKey: "horm_summary_mood",   Icon: Smile       },
  { key: "energy", labelKey: "horm_summary_energy", Icon: Zap         },
  { key: "libido", labelKey: "horm_summary_libido", Icon: Heart       },
  { key: "pain",   labelKey: "horm_summary_pain",   Icon: Thermometer },
];

const PANEL_MARKERS  = ['LH', 'FSH', 'E2', 'P4'];
const PANEL_SHORTS   = { LH: 'Lut', FSH: 'Fol', E2: 'E2h', P4: 'P4' };
const PANEL_ALIASES  = { LH: ['lut'], FSH: ['fol'], E2: ['e2h', 'e2'], P4: ['p4'] };

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusColor(theme, status) {
  if (!status) return theme.palette.grey[400];
  if (status.toUpperCase() === "NORMAL") return theme.palette.traffic.green;
  return theme.palette.traffic.red;
}

// ─── Gauge Bar ────────────────────────────────────────────────────────────────

function GaugeBar({ value, low, high }) {
  const theme = useTheme();
  if (value == null || (low == null && high == null)) return null;

  const val = Number(value);
  const lo  = low  != null ? Number(low)  : null;
  const hi  = high != null ? Number(high) : null;

  let dMin, dMax;
  if (lo != null && hi != null) {
    const range = (hi - lo) || 1;
    dMin = lo - range * 0.5;
    dMax = hi + range * 0.5;
  } else if (lo != null) {
    dMin = Math.min(0, lo * 0.5);
    dMax = Math.max(lo * 2, val * 1.2);
  } else {
    dMin = Math.min(0, val * 0.5);
    dMax = Math.max(hi * 1.5, val * 1.2);
  }

  const pad = (dMax - dMin) * 0.05 || 1;
  dMin = Math.min(dMin, val - pad);
  dMax = Math.max(dMax, val + pad);

  const span    = dMax - dMin;
  const clamp   = (v) => Math.max(0, Math.min(100, ((v - dMin) / span) * 100));
  const lowPct  = lo != null ? clamp(lo) : 0;
  const highPct = hi != null ? clamp(hi) : 100;
  const valPct  = clamp(val);
  const isNormal = (lo == null || val >= lo) && (hi == null || val <= hi);

  return (
    <Box sx={{ position: "relative", width: "100%", pb: 1 }}>
      <Box sx={{ position: "relative", width: "100%", height: 5, bgcolor: "action.disabledBackground", borderRadius: 0.5 }}>
        <Box sx={{
          position: "absolute",
          left: `${lowPct}%`,
          width: `${highPct - lowPct}%`,
          height: "100%",
          bgcolor: theme.palette.traffic.green,
          opacity: 0.4,
        }} />
        <Box sx={{
          position: "absolute",
          top: "50%",
          left: `${valPct}%`,
          transform: "translate(-50%, -50%)",
          width: 4,
          height: 14,
          bgcolor: isNormal ? theme.palette.traffic.green : theme.palette.traffic.red,
        }} />
      </Box>
      {lo != null && (
        <Typography variant="caption" color="text.disabled" sx={{
          position: "absolute", top: "100%", left: `${lowPct}%`,
          transform: "translateX(-50%)", fontSize: "0.6rem",
          fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
        }}>
          {lo.toFixed(2)}
        </Typography>
      )}
      {hi != null && (
        <Typography variant="caption" color="text.disabled" sx={{
          position: "absolute", top: "100%", left: `${highPct}%`,
          transform: "translateX(-50%)", fontSize: "0.6rem",
          fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
        }}>
          {hi.toFixed(2)}
        </Typography>
      )}
    </Box>
  );
}

// ─── Female profile label maps ────────────────────────────────────────────────

const PROFILE_LABEL = {
  cycletracker:  "horm_profile_cycletracker",
  perimenopause: "horm_profile_perimenopause",
  postmenopause: "horm_profile_postmenopause",
};

const STATUS_LABEL = {
  hormonal_birth_control: "horm_status_hormonal_bc",
  hrt:                    "horm_status_hrt",
};

// profiles where cycle day/phase should be fetched and shown
const CYCLE_PROFILES = new Set(["cycletracker", "perimenopause"]);

// ─── Imported hormone report (hormone-summaries with source_report_id) ───────

// hormones[].rolle → chart panel marker id
const ROLE_TO_PANEL = { lh: "LH", fsh: "FSH", e2_estradiol: "E2", p4_progesteron: "P4" };

// topics are keyed by id; known ids get a title + icon, unknown ids fall back to a generic tile.
// "feel" topics render under the wellbeing heading, everything else under "Dein Körper".
const TOPIC_META = {
  beschwerden_dieser_phase:     { group: "feel", Icon: Thermometer },
  sexualitaet_und_wohlbefinden: { group: "feel", Icon: Heart },
  knochen_und_muskeln:          { group: "body", Icon: Dumbbell },
  stoffwechsel_und_gefaesse:    { group: "body", Icon: Activity },
};

const hasBlock = (b) => !!(b?.text || b?.punkte?.length);

function topicTitle(t, key) {
  const k = `horm_topic_${key}`;
  const label = t(k);
  if (label !== k) return label;
  const words = key.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function useReportTokens() {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const rust = dark ? "hsl(18, 70%, 68%)" : "hsl(18, 62%, 38%)";
  return {
    dark,
    rust,
    ink:  dark ? theme.palette.grey.C50 : theme.palette.greydark.C900,
    body: dark ? theme.palette.grey.C400 : theme.palette.greydark.C500,
    peach: alpha(rust, dark ? 0.12 : 0.08),
    // same surface as the start card above, so new sections read as one page
    card: {
      borderRadius: "24px",
      bgcolor: dark ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
      backdropFilter: "blur(28px) saturate(180%)",
      WebkitBackdropFilter: "blur(28px) saturate(180%)",
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
      boxShadow: dark
        ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
        : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
    },
    tint: dark ? alpha("#fff", 0.04) : alpha(theme.palette.greydark.C900, 0.035),
    inner: dark ? alpha("#fff", 0.05) : "#fff",
  };
}

function Kicker({ children, sx }) {
  const { rust } = useReportTokens();
  return (
    <Typography component="span" sx={{
      fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em",
      textTransform: "uppercase", color: rust, lineHeight: 1.4, ...sx,
    }}>
      {children}
    </Typography>
  );
}

function SectionHeading({ kicker, title }) {
  const { ink } = useReportTokens();
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", columnGap: 1.5, rowGap: 0.25, flexWrap: "wrap", px: 0.5, mb: 2 }}>
      {kicker && <Kicker>{kicker}</Kicker>}
      <Typography component="h3" sx={{ fontSize: "1.15rem", fontWeight: 700, color: ink, lineHeight: 1.3 }}>
        {title}
      </Typography>
    </Box>
  );
}

function Paragraphs({ text, sx }) {
  const { body } = useReportTokens();
  if (!text) return null;
  return (
    <Typography sx={{ color: body, fontSize: "1rem", lineHeight: 1.75, whiteSpace: "pre-line", ...sx }}>
      {text}
    </Typography>
  );
}

function PointList({ points, sx }) {
  const { rust, ink } = useReportTokens();
  if (!points?.length) return null;
  return (
    <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: 1.25, ...sx }}>
      {points.map((p, i) => (
        <Box component="li" key={i} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
          <Box aria-hidden sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: rust, flexShrink: 0, mt: "0.6em" }} />
          <Typography sx={{ color: ink, fontSize: "0.95rem", lineHeight: 1.6 }}>{p}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/* Highlighted side panel: kicker + bullet points ("Bei dir" / "Für dich") */
function PointsPanel({ label, points, surface }) {
  const { peach } = useReportTokens();
  if (!points?.length) return null;
  return (
    <Box sx={{ bgcolor: surface ?? peach, borderRadius: "16px", p: { xs: 2, sm: 2.5 }, alignSelf: "start" }}>
      <Kicker sx={{ display: "block", mb: 1.25 }}>{label}</Kicker>
      <PointList points={points} />
    </Box>
  );
}

/* Body text left, points panel right — stacks on narrow screens */
function TextWithPanel({ block, label, surface }) {
  const hasPoints = !!block?.punkte?.length;
  return (
    <Box sx={{
      display: "grid", gap: { xs: 2, md: 3.5 },
      gridTemplateColumns: { xs: "1fr", md: hasPoints && block?.text ? "minmax(0, 1.4fr) minmax(0, 1fr)" : "1fr" },
    }}>
      {block?.text && <Paragraphs text={block.text} />}
      {hasPoints && <PointsPanel label={label} points={block.punkte} surface={surface} />}
    </Box>
  );
}

function IconTile({ Icon, variant }) {
  const theme = useTheme();
  const { rust, dark, inner } = useReportTokens();
  const round = variant === "round";
  return (
    <Box aria-hidden sx={{
      width: round ? 40 : 44, height: round ? 40 : 44, flexShrink: 0,
      borderRadius: round ? "50%" : "10px",
      display: "flex", alignItems: "center", justifyContent: "center",
      ...(round
        ? { bgcolor: inner }
        : { border: "1px solid", borderColor: dark ? "rgba(255,255,255,0.12)" : alpha(theme.palette.greydark.C900, 0.12) }),
    }}>
      <Icon size={18} strokeWidth={1.75} color={round ? rust : theme.palette.tertiary.C500} />
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function HormoneDetailsFemale() {
  const theme    = useTheme();
  const bodyColor = theme.palette.mode === "dark" ? theme.palette.grey.C400 : theme.palette.greydark.C500;
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const rt = useReportTokens();
  const { state } = useAuth();
  const gender  = state.user?.gender;
  const userAge = state.user?.age;

  const [visitOptions, setVisitOptions]           = useState([]);
  const [selectedId, setSelectedId]               = useState(null);
  const [reportError, setReportError]             = useState(false);
  const [datesLoading, setDatesLoading]           = useState(true);
  const [chartOpen, setChartOpen]                 = useState(false);
  const [chartMarker, setChartMarker]             = useState(null);
  const [openSubs, setOpenSubs]                   = useState(() => new Set());
  const [cycleDay, setCycleDay]                   = useState(null);
  const [cyclePhase, setCyclePhase]               = useState(null);
  const [infoOpen, setInfoOpen]                   = useState(false);
  const [infoMarker, setInfoMarker]               = useState(null);
  const [femaleProfile, setFemaleProfile]         = useState(null);   // { profile, status } | null
  const [femaleProfileLoading, setFemaleProfileLoading] = useState(true);
  const [profileInfoOpen, setProfileInfoOpen]     = useState(false);
  const [profileInfoSections, setProfileInfoSections] = useState(new Set());
  const [selectedHormoneMarker, setSelectedHormoneMarker] = useState('LH');
  const [hormoneSummaries, setHormoneSummaries]           = useState([]);

  /* ── Fetch female hormonal profile ─────────────────────────────────────── */
  useEffect(() => {
    if (gender !== "F") { setFemaleProfileLoading(false); return; }
    axios.get(`${API_URL}/api/female/profile`)
      .then(({ data }) => setFemaleProfile({ profile: data.profile, status: data.status ?? "normal" }))
      .catch(err => {
        // 404 = no profile created yet — show setup CTA
        if (err.response?.status !== 404) console.error("Female profile fetch failed", err);
        setFemaleProfile(null);
      })
      .finally(() => setFemaleProfileLoading(false));
  }, [gender]);

  /* ── Fetch cycle data for selected visit (cycletracker + perimenopause) ── */
  useEffect(() => {
    const visitDate = visitOptions.find(v => v.id === selectedId)?.visit_date;
    if (!visitDate || !CYCLE_PROFILES.has(femaleProfile?.profile)) {
      setCycleDay(null);
      setCyclePhase(null);
      return;
    }
    axios.get(`${API_URL}/api/cycles/by-date/?target_date=${visitDate}`)
      .then(res => {
        setCycleDay(res.data.cycle_day ?? null);
        setCyclePhase(res.data.cycle_phase ?? null);
      })
      .catch(() => { setCycleDay(null); setCyclePhase(null); });
  }, [selectedId, visitOptions, femaleProfile]);

  /* ── Fetch all lab visits with results ──────────────────────────────────── */
  useEffect(() => {
    const fetchVisits = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/all-lab-visits/results/`);
        const raw = res.data;
        const labVisits = Array.isArray(raw) ? raw : (raw.lab_visits || []);
        const completed = labVisits.filter((v) => Number(v.state) === 6).sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date));
        setVisitOptions(completed);
        if (completed.length > 0) setSelectedId((prev) => prev ?? completed[0].id);
      } catch (err) {
        console.error("Failed to fetch lab visits", err);
        setReportError("fetch");
      } finally {
        setDatesLoading(false);
      }
    };
    fetchVisits();
  }, []);

  /* ── Fetch hormone summaries ─────────────────────────────────────────────── */
  useEffect(() => {
    axios.get(`${API_URL}/hormone-summaries/`)
      .then(({ data }) => setHormoneSummaries(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  /* ── Derive report error from selected visit ─────────────────────────────── */
  useEffect(() => {
    if (!selectedId || !visitOptions.length) return;
    const v = visitOptions.find((v) => v.id === selectedId);
    setReportError(v?.results?.length ? false : "empty");
  }, [selectedId, visitOptions]);

  /* ── Derived data ─────────────────────────────────────────────────────────── */

  const report = visitOptions.find((v) => v.id === selectedId) ?? null;

  const hormoneResults = useMemo(
    () => (report?.results ?? []).filter(r => r.marker_category === "Hormones"),
    [report]
  );

  const groupedBySubsystem = useMemo(() => {
    const map = {};
    for (const m of hormoneResults) {
      const key = m.marker_healthsubsystem?.trim() ?? "__none__";
      if (!map[key]) map[key] = [];
      map[key].push(m);
    }
    Object.values(map).forEach(g => g.sort((a, b) => (a.marker_order ?? 0) - (b.marker_order ?? 0)));
    return map;
  }, [hormoneResults]);

  /* ── Loading / empty states ──────────────────────────────────────────────── */

  if (datesLoading) {
    return (
      <Backdrop open sx={{ zIndex: t => t.zIndex.drawer + 1 }}>
        <CircularProgress />
      </Backdrop>
    );
  }

  if (visitOptions.length === 0) {
    return (
      <Box sx={{ textAlign: "center", mt: 6 }}>
        <Typography color="text.secondary">{t("bt_no_visits")}</Typography>
      </Box>
    );
  }

  /* ── Subsystem order ─────────────────────────────────────────────────────── */

  const subsystemKeys = Object.keys(groupedBySubsystem).sort((a, b) => {
    if (a === "__none__") return 1;
    if (b === "__none__") return -1;
    return a.localeCompare(b);
  });

  const hasResults = report && !reportError;
  const headingColor = theme.palette.mode === "dark" ? "grey.C50" : "greydark.C900";
  const metaLabelSx = {
    fontSize: "0.85rem", fontWeight: 600, lineHeight: 1.3,
    color: theme.palette.mode === "dark" ? "grey.C400" : "greydark.C500",
  };
  const detailValueSx = { fontSize: "0.98rem", fontWeight: 600, lineHeight: 1.35, color: headingColor };
  const isFemale   = gender === "F";
  const showCharts = isFemale && hasResults && femaleProfile !== null;

  const hormoneSummary = hormoneSummaries.find(s => s.date === report?.visit_date) ?? null;
  // Imported report (source_report_id set) carries the new read-only sections; legacy records don't
  const importedReport = hormoneSummary?.source_report_id ? hormoneSummary : null;
  const topicEntries   = Object.entries(importedReport?.topics ?? {}).filter(([, b]) => hasBlock(b));
  const feelTopics     = topicEntries.filter(([k]) => TOPIC_META[k]?.group === "feel");
  const bodyTopics     = topicEntries.filter(([k]) => TOPIC_META[k]?.group !== "feel");
  const discussPoints  = (importedReport?.discuss?.punkte ?? []).filter(p => p?.thema || p?.warum);

  const aiSummary = (() => {
    const imported = importedReport?.hormones?.find(h => ROLE_TO_PANEL[h.rolle] === selectedHormoneMarker);
    if (imported?.summary) return imported.summary;
    const aliases = PANEL_ALIASES[selectedHormoneMarker] ?? [];
    const result = hormoneResults.find(r => {
      const s = r.marker_short?.toLowerCase() ?? '';
      return s === selectedHormoneMarker.toLowerCase() || aliases.includes(s);
    });
    return result?.ai_summary ?? null;
  })();

  /* ── Render ───────────────────────────────────────────────────────────────── */

  return (
    <Grid container justifyContent="center" sx={{ mt: 1, mb: 10 }}>

      {/* ── Start card: lab visit + date dropdown | hormone status with cycle ── */}
      <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2, mb: 3 }}>
        <Box sx={{
          width: "100%", maxWidth: 800,
          p: { xs: 2.25, sm: 3 }, borderRadius: "24px",
          bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
          backdropFilter: "blur(28px) saturate(180%)",
          WebkitBackdropFilter: "blur(28px) saturate(180%)",
          border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
          boxShadow: theme.palette.mode === "dark"
            ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
            : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1.15fr)" },
          columnGap: 4, rowGap: 2.5, alignItems: "start",
        }}>
          {/* Left: title + visit date dropdown */}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: headingColor, letterSpacing: -0.05 }}>
                {t("bt_labvisit")}
              </Typography>
              <Chip
                label={report?.visit_number ?? "—"}
                size="small"
                sx={{
                  bgcolor: theme.palette.mode === "dark" ? theme.palette.grey.C50    : theme.palette.greydark.C900,
                  color:   theme.palette.mode === "dark" ? theme.palette.greydark.C900 : theme.palette.grey.C50,
                  fontWeight: 600, fontSize: "0.8rem",
                }}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <DateSelector
                dates={visitOptions.map((v) => v.visit_date)}
                selectedDate={report?.visit_date}
                onSelect={(date) => setSelectedId(visitOptions.find((v) => v.visit_date === date)?.id ?? selectedId)}
              />
            </Box>
          </Box>

          {/* Right: hormone status and cycle, right-aligned values */}
          <Box component="dl" sx={{ m: 0, display: "flex", flexDirection: "column" }}>
            {[
              {
                key: "status",
                label: t("horm_profile_label"),
                value: femaleProfileLoading ? (
                  <Typography sx={detailValueSx}>…</Typography>
                ) : femaleProfile ? (
                  <>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={detailValueSx}>{t(PROFILE_LABEL[femaleProfile.profile] ?? femaleProfile.profile)}</Typography>
                      {femaleProfile.status !== "normal" && STATUS_LABEL[femaleProfile.status] && (
                        <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", lineHeight: 1.4 }}>
                          {t(STATUS_LABEL[femaleProfile.status])}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title={t("horm_info_dialog_title")} placement="top">
                      <IconButton size="small" aria-label={t("horm_info_dialog_title")} onClick={() => setProfileInfoOpen(true)} sx={{ color: "text.secondary" }}>
                        <Info size={18} strokeWidth={2.25} />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  /* No profile yet — set it up in the account settings */
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.85rem", color: "text.secondary", lineHeight: 1.4 }}>{t("horm_profile_not_set")}</Typography>
                    <ButtonBase
                      onClick={() => navigate("/UserAdminWelcome")}
                      sx={{
                        mt: 0.5, px: 1.25, py: 0.5, borderRadius: "10px",
                        fontSize: "0.82rem", fontWeight: 600, color: "primary.main",
                        border: "1px solid", borderColor: "primary.main",
                        transition: "background-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
                        "@media (hover: hover) and (pointer: fine)": { "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) } },
                        "&:active": { transform: "scale(0.97)" },
                      }}
                    >
                      {t("horm_profile_setup_cta")}
                    </ButtonBase>
                  </Box>
                ),
              },
              ...(femaleProfile && CYCLE_PROFILES.has(femaleProfile.profile) ? [{
                key: "cycle",
                label: t("bt_cycle_status"),
                value: (
                  <Typography sx={detailValueSx}>
                    {cyclePhase ? t(`horm_phase_${cyclePhase}`) : "—"}
                    {cycleDay != null && (
                      <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
                        {" · "}{t("horm_cycle_day")} {cycleDay}
                      </Box>
                    )}
                  </Typography>
                ),
              }] : []),
            ].map((row, i) => (
              <Box key={row.key}>
                {i > 0 && <Divider sx={{ borderColor: alpha(theme.palette.mode === "dark" ? "#fff" : theme.palette.greydark.C900, 0.08) }} />}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, minHeight: 52, py: 0.75, ...(i === 0 && { pt: 0, minHeight: 44 }) }}>
                  <Typography component="dt" sx={metaLabelSx}>{row.label.replace(/:\s*$/, "")}:</Typography>
                  <Box component="dd" sx={{ m: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5, minWidth: 0, textAlign: "right" }}>
                    {row.value}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Grid>

      {/* ── Error / empty state ───────────────────────────────────────────── */}
      {reportError && (
        <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2 }}>
          <Box sx={{
            width: "100%", maxWidth: 800,
            p: 2.5, borderRadius: 3,
            border: "0.5px solid", borderColor: "divider",
            textAlign: "center",
          }}>
            <Typography color="text.secondary">
              {reportError === "empty" ? t("bt_no_results_yet") : t("bt_results_error")}
            </Typography>
          </Box>
        </Grid>
      )}

      {/* ── Report summary: "Das Wichtigste" ──────────────────────────────── */}
      {hasResults && hasBlock(importedReport?.summary) && (
        <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2, mb: 4 }}>
          <Box component="section" sx={{ ...rt.card, width: "100%", maxWidth: 800, p: { xs: 2.25, sm: 3.5 } }}>
            <Typography component="h2" sx={{ fontSize: "1.35rem", fontWeight: 700, color: rt.ink, mb: 1.5, letterSpacing: "-0.01em" }}>
              {t("horm_rep_summary_title")}
            </Typography>
            <TextWithPanel block={importedReport.summary} label={t("horm_rep_for_you")} />
          </Box>
        </Grid>
      )}

      {/* ── Hormone charts: LH, FSH, E2, P4 ─────────────────────────────────── */}
      {showCharts && (
        <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2 }}>
          <Box sx={{ width: "100%", maxWidth: 800 }}>

            {/* Section title + subtitle */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 0.5, pt: 1, mb: 0.5 }}>
              <Typography variant="body1" fontWeight={700} sx={{
                color: theme.palette.mode === "dark" ? "grey.C50" : "greydark.C900", whiteSpace: "nowrap",
              }}>
                {t("horm_panel_title")}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{
              color: "text.secondary", fontWeight: 500,
              display: "block", px: 0.5, mb: 1.5,
            }}>
              {t("horm_panel_subtitle")}
            </Typography>

            {/* Marker selector */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2.5, mb: 3 }}>
              {PANEL_MARKERS.map(marker => {
                const isSelected = selectedHormoneMarker === marker;
                return (
                  <Box
                    key={marker}
                    component="button"
                    onClick={() => setSelectedHormoneMarker(marker)}
                    sx={{
                      px: 2.5, py: 0.75,
                      borderRadius: 99,
                      cursor: "pointer",
                      border: `1px solid ${isSelected
                        ? alpha(theme.palette.primary.main, 0.4)
                        : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)")}`,
                      bgcolor: isSelected
                        ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.16)
                        : (theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5)),
                      backdropFilter: "blur(20px) saturate(180%)",
                      WebkitBackdropFilter: "blur(20px) saturate(180%)",
                      boxShadow: theme.palette.mode === "dark"
                        ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 4px 16px rgba(0,0,0,0.4)"
                        : "inset 0 1px 0 rgba(255,255,255,0.7), 0 2px 12px rgba(0,0,0,0.05)",
                      color: isSelected ? (theme.palette.mode === "dark" ? "grey.C50" : "greydark.C900") : "text.secondary",
                      fontSize: "0.75rem",
                      fontWeight: isSelected ? 700 : 500,
                      fontFamily: "inherit",
                      lineHeight: 1.5,
                      outline: "none",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: alpha(theme.palette.primary.main, 0.5) },
                    }}
                  >
                    {t(`markername_${PANEL_SHORTS[marker]}`)}
                  </Box>
                );
              })}
            </Box>

            <HormoneChartPanel
              femaleProfile={femaleProfile}
              cycleDay={cycleDay}
              cyclePhase={cyclePhase}
              hormoneResults={hormoneResults}
              userAge={userAge}
              language={language}
              selectedMarkerId={selectedHormoneMarker}
              t={t}
              onOpenInfo={(markerShort) => { setInfoMarker(markerShort); setInfoOpen(true); }}
              onOpenChart={(markerShort) => { setChartMarker(markerShort); setChartOpen(true); }}
              aiSummary={aiSummary}
            />
          </Box>
        </Grid>
      )}

      {/* ── Report tail: wellbeing, doctor, androgens, body, steps, discuss ── */}
      {hasResults && hormoneSummary && (
        <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2, mt: 2 }}>
          <Box sx={{ width: "100%", maxWidth: 800 }}>

            {/* ── Wellbeing — imported report topics ────────────────────── */}
            {feelTopics.length > 0 && (
              <Box component="section" sx={{ ...rt.card, p: { xs: 2.25, sm: 3.5 } }}>
                <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: 700, color: rt.ink, pb: 2 }}>
                  {t("horm_summary_wellbeing_title")}
                </Typography>
                <Stack divider={<Divider sx={{ borderColor: alpha(rt.dark ? "#fff" : theme.palette.greydark.C900, 0.08) }} />}>
                  {feelTopics.map(([key, block]) => (
                    <Box key={key} sx={{ py: 2.5, "&:last-of-type": { pb: 0 } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.75 }}>
                        <IconTile Icon={TOPIC_META[key]?.Icon ?? CircleDot} />
                        <Typography component="h4" sx={{ fontSize: "1.05rem", fontWeight: 600, color: rt.ink }}>
                          {topicTitle(t, key)}
                        </Typography>
                      </Box>
                      <Paragraphs text={block.text} />
                      <PointList points={block.punkte} sx={{ mt: block.text ? 2 : 0 }} />
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* ── Wellbeing summary accordion (legacy records) ──────────── */}
            {!importedReport && SUMMARY_DIMS.some(d => hormoneSummary?.[d.key]) && (
              <Box sx={{
                borderRadius: 4,
                overflow: "hidden",
                bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
                backdropFilter: "blur(28px) saturate(180%)",
                WebkitBackdropFilter: "blur(28px) saturate(180%)",
                border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
                boxShadow: theme.palette.mode === "dark"
                  ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
                  : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
              }}>
                <Typography variant="body1" fontWeight={700} sx={{
                  color: theme.palette.mode === "dark" ? "grey.C50" : "greydark.C900",
                  px: 2.5, pt: 2.5, pb: 1.5, display: "block",
                }}>
                  {(() => {
                    const [titleMain, titleSuffix] = t("horm_summary_wellbeing_title").split(" — ");
                    return (
                      <>
                        {titleMain}
                        {titleSuffix && (
                          <Box component="span" sx={{ display: { xs: "block", sm: "inline" } }}>
                            {" — "}{titleSuffix}
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </Typography>
                <Stack divider={<Divider sx={{ mx: 2.5, opacity: 0.5 }} />}>
                  {SUMMARY_DIMS.filter(d => hormoneSummary?.[d.key]).map(({ key, labelKey, Icon }) => (
                    <Accordion key={key} disableGutters elevation={0} defaultExpanded={false} sx={{ bgcolor: "transparent", boxShadow: "none", "&:before": { display: "none" } }}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: "text.disabled" }} />}
                        sx={{
                          px: 2.5,
                          minHeight: 56,
                          "& .MuiAccordionSummary-content": { my: 1, display: "flex", alignItems: "center", gap: 1.5 },
                        }}
                      >
                        <Box sx={{
                          width: 32, height: 32, borderRadius: 2, flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          bgcolor: "transparent",
                          border: "1px solid", borderColor: "divider",
                        }}>
                          <Icon size={18} color={theme.palette.tertiary.C300} />
                        </Box>
                        <Typography variant="subtitle1" sx={{ lineHeight: 1.1, color: theme.palette.mode === "dark" ? "grey.C50" : "greydark.C900", fontWeight: 500 }}>
                          {t(labelKey)}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
                        <Typography variant="body1" sx={{ color: bodyColor, lineHeight: 1.75, fontWeight: 400 }}>
                          {hormoneSummary[key]}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              </Box>
            )}

            {/* ── Talk to Doctor box (legacy only — imported reports use "Arztgespräch" below) ── */}
            {!importedReport && hormoneSummary?.doctor_question && (
              <Box sx={{
                mt: 1.5,
                p: 2.25,
                borderRadius: "12px",
                overflow: "hidden",
                bgcolor: theme.palette.mode === "dark"
                  ? alpha(theme.palette.quartiary.C500, 0.12)
                  : alpha(theme.palette.quartiary.C300, 0.14),
                backdropFilter: "blur(20px) saturate(160%)",
                WebkitBackdropFilter: "blur(20px) saturate(160%)",
                border: `1px solid ${theme.palette.mode === "dark" ? alpha(theme.palette.quartiary.C400, 0.3) : alpha(theme.palette.quartiary.C300, 0.35)}`,
                boxShadow: theme.palette.mode === "dark"
                  ? "inset 0 1px 0 rgba(255,255,255,0.05), 0 4px 16px rgba(0,0,0,0.4)"
                  : "inset 0 1px 0 rgba(255,255,255,0.5), 0 2px 12px rgba(0,0,0,0.04)",
              }}>
                <Typography variant="body1" fontWeight={700} sx={{
                  display: "block", mb: 0.75,
                  color: theme.palette.mode === "dark" ? theme.palette.quartiary.C300 : theme.palette.quartiary.C700,
                }}>
                  {t("horm_summary_doctor")}
                </Typography>
                <Typography sx={{
                  fontSize: "0.85rem", lineHeight: 1.75, fontStyle: "italic",
                  color: theme.palette.mode === "dark" ? theme.palette.grey.C300 : theme.palette.greydark.C700,
                }}>
                  "{hormoneSummary.doctor_question}"
                </Typography>
              </Box>
            )}

            {/* ── Androgens: Testosteron, SHBG, DHEA-S ──────────────────── */}
            {hasBlock(importedReport?.androgens) && (
              <Box component="section" sx={{ mt: 6 }}>
                <SectionHeading kicker={t("horm_rep_androgens_kicker")} title={t("horm_rep_androgens_title")} />
                <Box sx={{ px: 0.5 }}>
                  <TextWithPanel block={importedReport.androgens} label={t("horm_rep_for_you")} />
                </Box>
              </Box>
            )}

            {/* ── Body topics: bones, metabolism, + any unknown topic ids ── */}
            {bodyTopics.length > 0 && (
              <Box component="section" sx={{ mt: 6 }}>
                <SectionHeading kicker={t("horm_rep_body_kicker")} title={t("horm_rep_body_title")} />
                <Stack spacing={2}>
                  {bodyTopics.map(([key, block]) => (
                    <Box key={key} sx={{ bgcolor: rt.tint, borderRadius: "20px", p: { xs: 2.25, sm: 3 } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, mb: 1.75 }}>
                        <IconTile Icon={TOPIC_META[key]?.Icon ?? CircleDot} variant="round" />
                        <Typography component="h4" sx={{ fontSize: "1.1rem", fontWeight: 700, color: rt.ink }}>
                          {topicTitle(t, key)}
                        </Typography>
                      </Box>
                      <TextWithPanel block={block} label={t("horm_rep_for_you")} surface={rt.inner} />
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* ── Next steps ────────────────────────────────────────────── */}
            {hasBlock(importedReport?.actions) && (
              <Box component="section" sx={{ mt: 6 }}>
                <SectionHeading kicker={t("horm_rep_actions_kicker")} title={t("horm_rep_actions_title")} />
                <Box sx={{ px: 0.5 }}>
                  <TextWithPanel block={importedReport.actions} label={t("horm_rep_for_you_actions")} />
                </Box>
              </Box>
            )}

            {/* ── Topics for the doctor's appointment ───────────────────── */}
            {(importedReport?.discuss?.text || discussPoints.length > 0) && (
              <Box component="section" sx={{ mt: 6 }}>
                <SectionHeading kicker={t("horm_rep_discuss_kicker")} title={t("horm_rep_discuss_title")} />
                <Box sx={{ px: 0.5 }}>
                  <Paragraphs text={importedReport.discuss.text} sx={{ mb: 1.5 }} />
                  {discussPoints.length > 0 && (
                    <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
                      {discussPoints.map((p, i) => (
                        <Box component="li" key={i} sx={{
                          display: "flex", gap: 2, alignItems: "flex-start", py: 2,
                          borderTop: "1px solid",
                          borderColor: alpha(rt.dark ? "#fff" : theme.palette.greydark.C900, 0.1),
                        }}>
                          <Box aria-hidden sx={{
                            width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                            bgcolor: rt.rust, color: rt.dark ? theme.palette.greydark.C900 : "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: "0.85rem", fontWeight: 700, fontVariantNumeric: "tabular-nums",
                          }}>
                            {i + 1}
                          </Box>
                          <Box sx={{ minWidth: 0, pt: "3px" }}>
                            {p.thema && (
                              <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: rt.ink, lineHeight: 1.4, mb: 0.5 }}>
                                {p.thema}
                              </Typography>
                            )}
                            {p.warum && (
                              <Typography sx={{ fontSize: "0.95rem", color: rt.body, lineHeight: 1.7 }}>
                                {p.warum}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      )}

      {/* ── Hormone markers ───────────────────────────────────────────────── */}
      {hasResults && (
        <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2, mt: 6, mb: 3 }}>
          <Box sx={{ width: "100%", maxWidth: 800 }}>

            {/* Section header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 0.5, pt: 1, mb: 1.5 }}>
              <Typography variant="body1" fontWeight={700} sx={{
                color: theme.palette.mode === "dark" ? "grey.C50" : "greydark.C900", whiteSpace: "nowrap",
              }}>
                {t("horm_marker_overview_heading")}
              </Typography>
            </Box>
            {subsystemKeys.length === 0 ? (
              <Box sx={{
                p: 2.5, borderRadius: 3,
                border: "1px solid", borderColor: "divider",
                textAlign: "center",
              }}>
                <Typography variant="body2" fontWeight={700} color="text.disabled" sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
                  {t("bt_not_ordered")}
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                  {t("bt_consider_addon")}
                </Typography>
              </Box>
            ) : (
              subsystemKeys.map((sk, subIdx) => {
                const markers     = groupedBySubsystem[sk];
                const subLabelKey = SUBSYSTEM_KEY[sk];
                const subLabel    = sk === "__none__" ? null : (subLabelKey ? t(subLabelKey) : sk);
                const redCount    = markers.filter(m => m.status && m.status.toUpperCase() !== "NORMAL").length;
                const isLastSubsystem = subIdx === subsystemKeys.length - 1;
                const dark        = theme.palette.mode === "dark";
                const subOpen     = openSubs.has(sk);
                const toggleSub   = () => {
                  setOpenSubs((prev) => {
                    const next = new Set(prev);
                    if (next.has(sk)) next.delete(sk); else next.add(sk);
                    return next;
                  });
                };
                return (
                  <Box key={sk} sx={{ mb: isLastSubsystem ? 0 : 2 }}>
                    <Accordion
                      expanded={subOpen}
                      onChange={toggleSub}
                      disableGutters
                      elevation={0}
                      slotProps={{ transition: { timeout: 320 } }}
                      sx={{
                        borderRadius: "12px !important",
                        overflow: "hidden",
                        "&:before": { display: "none" },
                        bgcolor: dark ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
                        backdropFilter: "blur(28px) saturate(180%)",
                        WebkitBackdropFilter: "blur(28px) saturate(180%)",
                        border: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.35)"}`,
                        boxShadow: dark
                          ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
                          : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={subOpen
                          ? <Minus size={18} color={theme.palette.mode === "light" ? theme.palette.greydark.C900 : theme.palette.grey.C50} />
                          : <Plus size={18} color={theme.palette.mode === "light" ? theme.palette.greydark.C900 : theme.palette.grey.C50} />}
                        sx={{
                          px: 2.5,
                          bgcolor: "transparent",
                          minHeight: 44,
                          "& .MuiAccordionSummary-content": { my: 0.5, display: "flex", alignItems: "center", gap: 1, pr: 2 },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {subLabel && (
                            <Typography variant="subtitle2" fontWeight={700}
                              sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
                              {subLabel}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ flexGrow: 1 }} />
                        {redCount > 0 ? (
                          <Tooltip title={t("bt_tooltip_out_of_range")} placement="top">
                            <Chip
                              label={`${redCount}`}
                              size="small"
                              sx={{
                                bgcolor: "transparent",
                                color: theme.palette.traffic.red,
                                border: "1px solid", borderColor: theme.palette.traffic.red,
                                fontWeight: 500, fontSize: "0.8rem",
                                width: 22, height: 22,
                                borderRadius: "999px",
                                "& .MuiChip-label": { px: 0, display: "flex", alignItems: "center", justifyContent: "center", width: "100%" },
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title={t("bt_tooltip_all_in_range")} placement="top">
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Check size={18} strokeWidth={2.5} color={theme.palette.traffic.green} />
                            </Box>
                          </Tooltip>
                        )}
                      </AccordionSummary>

                      <AccordionDetails sx={{ p: 0 }}>
                        {markers.map((m, i) => {
                          const statusKey  = `bt_status_${(m.status || "").toLowerCase().replace(/\s+/g, "_")}`;
                          const statusText = t(statusKey) !== statusKey ? t(statusKey) : m.status;
                          return (
                            <Box key={m.id}>
                              {i > 0 && <Divider sx={{ mx: 2 }} />}
                              <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 0.75, columnGap: 2, rowGap: { xs: 0.5, sm: 2 }, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flexGrow: 1, minWidth: 120 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: statusColor(theme, m.status), mt: "7px" }} />
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body1" fontWeight={500}>{t(`markername_${m.marker_short}`)}</Typography>
                                    <Typography variant="caption" color="text.secondary">{t(`markerdescr_${m.marker_short}`)}</Typography>
                                  </Box>
                                </Box>
                                {/* Mobile-only line break: forces the value+gauge pair onto their own row */}
                                <Box sx={{ flexBasis: "100%", height: 0, display: { xs: "block", sm: "none" } }} />
                                {/* Grouped into one flex item so a long unit label can't push the gauge
                                    onto its own separate line — the pair wraps as a unit, and is no
                                    longer hidden on mobile. */}
                                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap", ml: { xs: "26px", sm: 0 } }}>
                                  <Typography variant="number3" fontWeight={400} sx={{ minWidth: { xs: "auto", sm: 80 }, textAlign: { xs: "left", sm: "right" }, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                                    {m.value != null ? Number(m.value).toFixed(2) : "—"}{m.unit ? ` ${m.unit}` : ""}
                                  </Typography>
                                  <Box sx={{ minWidth: 140, ml: 2, display: "flex", alignItems: "center" }}>
                                    <GaugeBar value={m.value} low={m.ref_low} high={m.ref_high} />
                                  </Box>
                                </Box>
                                {/* Mobile-only line break: forces the check/info/chart icons onto their own row */}
                                <Box sx={{ flexBasis: "100%", height: 0, display: { xs: "block", sm: "none" } }} />
                                <Box sx={{ minWidth: { xs: "auto", sm: 68 }, ml: { xs: "26px", sm: 0 }, display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" }, flexShrink: 0 }}>
                                  {m.status?.toUpperCase() === "NORMAL"
                                    ? <Check size={18} strokeWidth={3} color={theme.palette.traffic.green} />
                                    : <Typography variant="subtitle2" fontWeight={700} sx={{ color: statusColor(theme, m.status) }}>{statusText}</Typography>
                                  }
                                </Box>
                                <Tooltip title={t("acc_visit_info_title")} placement="top">
                                  <IconButton size="small" onClick={() => { setInfoMarker(m.marker_short); setInfoOpen(true); }} sx={{ "&:hover": { transform: "scale(1.2)" } }}>
                                    <Info size={18} color={theme.palette.secondary.main} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t("bt_chart_history")} placement="top">
                                  <IconButton size="small" onClick={() => { setChartMarker(m.marker_short); setChartOpen(true); }} sx={{ "&:hover": { transform: "scale(1.2)" } }}>
                                    <ChartNoAxesCombined size={18} color={theme.palette.primary.main} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          );
                        })}
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                );
              })
            )}
          </Box>
        </Grid>
      )}

      {/* ── Chart history dialog ─────────────────────────────────────────── */}
      <CustomDialog
        open={chartOpen}
        onClose={() => setChartOpen(false)}
        title={chartMarker ? `${t(`markername_${chartMarker}`)} — ${t("bt_chart_title")}` : ""}
        content={chartMarker && <MarkerTrendChart markerShort={chartMarker} visitOptions={visitOptions} theme={theme} t={t} />}
      />

      {/* ── Info dialog ───────────────────────────────────────────────────── */}
      <CustomDialogSmall
        open={infoOpen}
        onClose={() => { setInfoOpen(false); setInfoMarker(null); }}
        title={t("acc_visit_info_title")}
        content={infoMarker && (
          <TextInfoComp
            description={`description_${infoMarker}`}
            lowtext={`lowtext_${infoMarker}`}
            lowrec={`lowrec_${infoMarker}`}
            rangetext={`rangetext_${infoMarker}`}
            hightext={`hightext_${infoMarker}`}
            highrec={`highrec_${infoMarker}`}
          />
        )}
      />

      {/* ── Profile info dialog ────────────────────────────────────────────── */}
      <CustomDialogSmall
        open={profileInfoOpen}
        onClose={() => setProfileInfoOpen(false)}
        title={t("horm_info_dialog_title")}
        content={
          <Box>
            {[
              {
                key: "profiles",
                label: t("horm_info_profiles_heading"),
                body: (
                  <>
                    {[
                      ["horm_info_cycletracker_title",  "horm_info_cycletracker_text"],
                      ["horm_info_perimenopause_title", "horm_info_perimenopause_text"],
                      ["horm_info_postmenopause_title", "horm_info_postmenopause_text"],
                      ["horm_info_hormonal_bc_title",   "horm_info_hormonal_bc_text"],
                      ["horm_info_hrt_title",           "horm_info_hrt_text"],
                    ].map(([titleKey, textKey], i, arr) => (
                      <Box key={titleKey} sx={{ mb: i === arr.length - 1 ? 0 : 1.5 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>{t(titleKey)}</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 400, lineHeight: 1.6 }}>{t(textKey)}</Typography>
                      </Box>
                    ))}
                  </>
                ),
              },
              {
                key: "charts",
                label: t("horm_info_charts_heading"),
                body: <Typography variant="subtitle1" sx={{ fontWeight: 400, lineHeight: 1.6 }}>{t("horm_info_charts_text")}</Typography>,
              },
              {
                key: "selfservice",
                label: t("horm_info_selfservice_heading"),
                body: <Typography variant="subtitle1" sx={{ fontWeight: 400, lineHeight: 1.6 }}>{t("horm_info_selfservice_text")}</Typography>,
              },
            ].map(({ key, label, body }) => {
              const isOpen = profileInfoSections.has(key);
              return (
                <Accordion
                  key={key}
                  expanded={isOpen}
                  onChange={(e, expandedNow) => {
                    setProfileInfoSections((prev) => {
                      const next = new Set(prev);
                      if (expandedNow) next.add(key); else next.delete(key);
                      return next;
                    });
                  }}
                  disableGutters
                  elevation={0}
                  square
                  sx={{
                    bgcolor: "transparent",
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    "&:last-of-type": { borderBottom: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={isOpen ? <Minus size={18} /> : <Plus size={18} />}
                    sx={{ px: 0 }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: isOpen ? 600 : 400 }}>{label}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0 }}>
                    {body}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        }
      />

    </Grid>
  );
}
